<!--
	SafeIntersections crash-risk calculator for a selected Identify intersection: original vs
	proposed model inputs, a countermeasure's crash-modification factor, and the resulting class and
	10-year crash estimate. Remounted per feature by Countermeasures.svelte's `{#key}`.
-->
<script lang="ts">
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import { convert } from '#lib/intlUnits.js';
	import type { MapGeoJSONFeature } from 'maplibre-gl';

	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { legendStore } from '#lib/stores/legendStore.svelte.js';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import InfoButton from '#lib/components/InfoButton.svelte';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import Select, { type SelectGroup } from '#lib/components/Select.svelte';
	import Slider from '#lib/components/Slider.svelte';
	import IconChevronDown from '#lib/assets/chevron-down.svg?raw';

	import {
		EIdentify,
		ESafeIntersectionsModule,
		type ISafeIntersectionsSummaryData
	} from '../interfaces';
	import type { TIdentify } from '../columns';
	import {
		approachDistBand,
		calculateCrashRisk,
		crashModificationFactors,
		ECountermeasures,
		getIntersectionClass,
		predictedCrashes10yr,
		qMajorBand,
		skewAngleBand
	} from './safeIntersectionsLib';

	const DEFAULT_QMAJOR = 50;

	// Band labels in slider order; representative values per band feed the crash-risk algorithm.
	const qMajorLabels = ['≤1,550', '1,551 - 4,250', '>4,250'];
	const sliderRepValuesQMajor = [DEFAULT_QMAJOR, 2000, 6000];
	const skewAngleLabels = ['≤140°', '141° - 165°', '>165°'];
	const sliderRepValuesSkewAngle = [110, 150, 175];
	// Labels in feet (display); representative values in metres (model input).
	const approachDistLabels = ['≤1,640 ft', '1,641 - 4,920 ft', '>4,920 ft'];
	const sliderRepValuesMinorApproachDistMetres = [300, 1000, 2000];

	const C = ECountermeasures;
	const countermeasureOptions: SelectGroup[] = (
		[
			[
				'Signage and delineation',
				[C.SignageDelineationUpgrade, C.AdvanceWarningSignage, C.SpeedLimitReduction]
			],
			[
				'Advanced awareness',
				[C.ActivatedWarningSigns, C.ActivatedWarningSignsWithSpeedLimitReduction, C.Lighting]
			],
			[
				'Transformational',
				[C.InstallRoundabout, C.StaggerJunctions, C.CloseIntersection, C.GradeSeparation]
			]
		] as [string, ECountermeasures[]][]
	).map(([label, values]) => ({
		label,
		options: values.map((value) => ({ label: value, value }))
	}));

	const formatNumber = (value: number | null | undefined, options?: Intl.NumberFormatOptions) =>
		value?.toLocaleString('en-US', options);

	interface Props {
		feature: MapGeoJSONFeature;
	}

	const { feature }: Props = $props();

	const data = $derived(feature.properties as TIdentify);

	// Colour source for the intersection-class patches.
	const legendItems = $derived(
		legendStore.layers.find((l) => l.systemId === EIdentify.Identify)?.legendItems ?? []
	);

	const riskBands = $derived.by(() => {
		const summary = geodataStore.summaryData as ISafeIntersectionsSummaryData | null;
		const identifyRow = summary?.[ESafeIntersectionsModule.Identify]?.[0];
		return {
			lower: identifyRow?.risk_band_lower as number | undefined,
			upper: identifyRow?.risk_band_upper as number | undefined
		};
	});

	const currentQMinor = $derived(data.qminor || 0);
	const currentQMajor = $derived(data.qmajor || DEFAULT_QMAJOR);
	const currentSkewAngle = $derived(data.minor_approach_skew_angle);
	const currentApproachLengthMetres = $derived(data.minor_approach_distance_m);
	const currentApproachLengthFeet = $derived(
		convert(currentApproachLengthMetres, 'length', 'metric', 'imperial')
	);

	const currentCrashRisk = $derived(
		calculateCrashRisk(currentQMinor, currentQMajor, currentSkewAngle, currentApproachLengthMetres)
	);
	const existingPredictedCrashes10yr = $derived(predictedCrashes10yr(currentCrashRisk));
	const existingIntersectionClass = $derived(
		getIntersectionClass(currentCrashRisk, riskBands.lower, riskBands.upper)
	);

	// Proposed inputs, initialised from the selected feature.
	let proposedQminor = $state(0);
	let proposedQmajor = $state(0);
	let proposedSkewAngle = $state(0);
	let proposedApproachLengthMetres = $state(0);
	// One-shot init; the `{#key}` remount re-runs it per feature.
	reset();

	function reset() {
		proposedQminor = currentQMinor;
		proposedQmajor = qMajorBand(currentQMajor);
		proposedSkewAngle = skewAngleBand(currentSkewAngle);
		proposedApproachLengthMetres = approachDistBand(currentApproachLengthMetres);
	}

	let countermeasure = $state<ECountermeasures | undefined>(undefined);

	const proposedCrashRate = $derived.by(() => {
		const proposedCrashRisk = calculateCrashRisk(
			proposedQminor,
			sliderRepValuesQMajor[proposedQmajor],
			sliderRepValuesSkewAngle[proposedSkewAngle],
			sliderRepValuesMinorApproachDistMetres[proposedApproachLengthMetres]
		);
		const crashes10Yr = predictedCrashes10yr(proposedCrashRisk);
		const crashReductionFactor = countermeasure ? crashModificationFactors[countermeasure] : 0;
		if (!crashes10Yr) return undefined;
		return crashes10Yr * (1 - crashReductionFactor);
	});
	const proposedCrashes10yr = $derived(
		formatNumber(proposedCrashRate, { maximumFractionDigits: 2 })
	);
	const proposedIntersectionClass = $derived(
		getIntersectionClass(proposedCrashRate, riskBands.lower, riskBands.upper)
	);

	const crashDelta = $derived((proposedCrashRate || 0) - (existingPredictedCrashes10yr || 0));

	function getPatchColour(label: string | undefined) {
		if (label) {
			return legendItems.find((l) => l.label === label)?.color ?? PATCH_FALLBACK;
		}
		return PATCH_FALLBACK;
	}

	function onCountermeasureChange(value: string) {
		countermeasure = value === '' ? undefined : (value as ECountermeasures);
	}
</script>

<div class="body">
	<details open>
		<summary>
			<h3>Features</h3>
			<span class="chevron">{@html IconChevronDown}</span>
		</summary>
		<div class="toolbar">
			<AbleyButton variant="text" onclick={reset}>Reset proposed</AbleyButton>
		</div>
		<table class="metricsTable featureTable">
			<thead>
				<tr>
					<th scope="col">Variable</th>
					<th scope="col">Original</th>
					<th scope="col" style:width="32%">Scenario/Testing</th>
					<th scope="col" style:width="18%">Proposed</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<th scope="row">
						<div class="infoContainer">
							<div>AADT: controlled legs</div>
							<InfoButton noBorder content="Average AADT of controlled legs" />
						</div>
					</th>
					<td>
						{#if data.qminor}
							{formatNumber(data.qminor)}
						{:else}
							<div class="infoContainer">
								<em>Unknown</em>
								<InfoButton noBorder content="AADT unknown at this location" />
							</div>
						{/if}
					</td>
					<td>
						<Slider
							min={1}
							max={10000}
							bind:value={proposedQminor}
							labels={['1', '10,000']}
							aria-label="Proposed AADT: controlled legs" />
					</td>
					<td>{formatNumber(proposedQminor)}</td>
				</tr>
				<tr>
					<th scope="row">
						<div class="infoContainer">
							<div>AADT: priority legs</div>
							<InfoButton noBorder content="Average AADT of priority legs" />
						</div>
					</th>
					<td>
						{#if data.qmajor}
							{formatNumber(data.qmajor, { maximumSignificantDigits: 3 })}
						{:else}
							<div class="infoContainer">
								<em>Unknown</em>
								<InfoButton
									noBorder
									content="AADT unknown at this location, assuming it is <=1,550" />
							</div>
						{/if}
					</td>
					<td>
						<Slider
							min={0}
							max={2}
							labels={[qMajorLabels[0], qMajorLabels[2]]}
							aria-label="Proposed AADT: priority legs"
							bind:value={proposedQmajor} />
					</td>
					<td>{qMajorLabels[proposedQmajor]}</td>
				</tr>
				<tr>
					<th scope="row">
						<div class="infoContainer">
							<div>Skew angle</div>
							<InfoButton
								noBorder
								content="Angle between minor legs (where 180 degrees indicates minor legs are in line)." />
						</div>
					</th>
					<td>{formatNumber(currentSkewAngle, { maximumSignificantDigits: 2 })}&nbsp;&deg;</td>
					<td>
						<Slider
							min={0}
							max={2}
							labels={[skewAngleLabels[0], skewAngleLabels[2]]}
							aria-label="Proposed skew angle"
							bind:value={proposedSkewAngle} />
					</td>
					<td>{skewAngleLabels[proposedSkewAngle]}</td>
				</tr>
				<tr>
					<th scope="row">
						<div class="infoContainer">
							<div>Length of straight</div>
							<InfoButton
								noBorder
								content="Length between intersection and nearest curve or other intersection, average value of both controlled legs." />
						</div>
					</th>
					<td
						>{formatNumber(currentApproachLengthFeet, { maximumSignificantDigits: 2 })}&nbsp;ft</td>
					<td>
						<Slider
							min={0}
							max={2}
							labels={[approachDistLabels[0], approachDistLabels[2]]}
							aria-label="Proposed length of straight"
							bind:value={proposedApproachLengthMetres} />
					</td>
					<td>{approachDistLabels[proposedApproachLengthMetres]}</td>
				</tr>
			</tbody>
		</table>
	</details>
	<div>
		<h3>Countermeasures</h3>
		<Select
			options={countermeasureOptions}
			value={countermeasure ?? ''}
			label="Countermeasure"
			hideLabel
			placeholder="None"
			onchange={onCountermeasureChange} />
	</div>
	<div>
		<h3>Metrics</h3>
		<table class="metricsTable">
			<thead>
				<tr>
					<th scope="col"><span class="screenReaderOnly">Metric</span></th>
					<th scope="col">Original</th>
					<th scope="col">Proposed</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<th scope="row">Intersection class</th>
					{#each [existingIntersectionClass, proposedIntersectionClass] as intersectionClass, i (i)}
						<td>
							<div class="rating">
								<LegendPatch color={getPatchColour(intersectionClass)} shape="round" />
								{intersectionClass}
							</div>
						</td>
					{/each}
				</tr>
				<tr>
					<th scope="row">Est. crashes (10 years)</th>
					<td>{formatNumber(existingPredictedCrashes10yr, { maximumFractionDigits: 2 })}</td>
					<td>
						{proposedCrashes10yr}
						{#if existingPredictedCrashes10yr !== undefined}
							(<span class:crashRed={crashDelta > 0} class:crashGreen={crashDelta < 0}
								>{crashDelta.toLocaleString('en-US', {
									maximumFractionDigits: 2,
									signDisplay: 'exceptZero'
								})}</span
							>)
						{/if}
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</div>

<style>
	.body {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		margin-top: var(--space-4);
		width: 100%;

		> * {
			padding: 0 var(--space-1) var(--space-2);

			@media screen and (min-width: 1100px) {
				padding: 0 var(--space-5) var(--space-3);
			}
		}

		h3 {
			margin-block: var(--space-2) var(--space-1);
			font-weight: var(--weight-bold);
			font-size: var(--font-size-base);
		}
	}

	summary {
		display: flex;
		align-items: center;
		cursor: pointer;
		list-style: none;

		&::-webkit-details-marker {
			display: none;
		}

		.chevron {
			transition: transform 0.15s ease-out;
			margin-inline: auto var(--space-2);
		}
	}

	details[open] .chevron {
		transform: rotate(180deg);
	}

	.toolbar {
		display: flex;
		justify-content: flex-end;
	}

	.metricsTable {
		margin-top: var(--space-2);
		width: 100%;
		border-collapse: collapse;
		font-size: var(--font-size-sm);

		tbody tr:nth-child(odd) {
			background-color: var(--surface-sunken);
		}

		td,
		th {
			padding: var(--space-2) var(--space-1) var(--space-2) var(--space-3);
			font-weight: var(--weight-regular);
			text-align: start;
		}

		thead th,
		th[scope='row'] {
			font-weight: var(--weight-semibold);
		}
	}

	.featureTable td {
		&:nth-child(3) {
			padding-top: var(--space-4);
			padding-right: var(--space-3);
		}

		&:last-child {
			text-align: center;
		}
	}

	.rating {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-weight: var(--weight-semibold);
	}

	.crashRed {
		color: var(--status-danger);
	}

	.crashGreen {
		color: var(--status-success);
	}

	.infoContainer {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}
</style>
